import getAllApplicationsForFamily from './data/getAllApplicationsForFamily';
import * as FamilyConstants from './FamilyConstants';

/**
 * @typedef {Object} StatForApplication
 * @property {string} applicationKey
 * @property {string} key
 * @property {string} label
 */
/**
 * @typedef {Object} ApplicationWithStats
 * @property {string} key
 * @property {string} label
 * @property {Array<StatForApplication>} stats
 */

/**
 * Returns stat metadata key and labels.
 *
 * @param {Object<string, import('../types').StatMetadata>} statMetadataForApplication
 * @returns {StatForApplication[]} Stat keys and labels, ordered by "section_order"
 */
function getFlattenedStatsForApplication(statMetadataForApplication) {
    if (!statMetadataForApplication) {
        return [];
    }

    return Object.entries(statMetadataForApplication)
        .filter(([, { status }]) => status === 'ACTIVE')
        .sort((a, b) => a[1].section_order - b[1].section_order)
        .map(([key, { label, scope }]) => ({
            key,
            label,
            scope,
        }));
}

/**
 * Returns a map of applications (keys/labels) and the active stats for each of the applications.
 *
 * @param {import('../types').Family[]} families
 * @param {number} familyId
 *
 * @returns {ApplicationWithStats[]}
 */
export default function getStatsByApplicationForFamilyById(families, familyId) {
    const family = families.find(({ id }) => id === familyId);
    if (!family) {
        return null;
    }

    const applications = getAllApplicationsForFamily(family, families);

    return applications.map((application) => {
        if (application.key === family.platform_family_key) {
            const platformFamily = families.find(({ key }) => key === application.key);
            let appLabel = application.label;
            if (platformFamily.key === FamilyConstants.VaultPlatformKey) {
                appLabel = FamilyConstants.VaultPlatformLabel;
            }
            return {
                key: application.key,
                label: appLabel,
                stats: getFlattenedStatsForApplication(
                    platformFamily.stat_metadata[platformFamily.key],
                ),
            };
        }

        return {
            key: application.key,
            label: application.label,
            stats: getFlattenedStatsForApplication(family.stat_metadata[application.key]),
        };
    });
}
